import React from 'react';
import { Button } from '../../components/ui/button';

/**
 * Right-side sticky cost summary shown on the Customer Info step.
 * Purely presentational — all numbers come from the parent.
 *
 * Props:
 *   itemCount      — integer; shown as "Total Items"
 *   subtotal       — number in the order's currency
 *   shipping       — number in the order's currency (matches Freightcom CAD rates)
 *   tax            — number; computed dollar amount of sales tax
 *   taxLabel       — string; province-specific label ("HST 13%", "GST 5%", "Tax exempt (US)")
 *                    falls back to "Tax" when no province has been detected yet
 *   total          — number in the order's currency (subtotal + shipping + tax)
 *   currency       — ISO currency code for the total label (default 'CAD')
 *   formatCurrency — (n) => string formatter shared with parent
 *
 * iter114s.60 — Tax line is now LIVE. Previously hard-coded to `--`,
 * we now display the actual computed tax + the province-specific
 * label as soon as the postal-code auto-detect (iter114s.59) fills
 * in `state_province`. The customer sees the real total before they
 * even reach Review Order.
 */
export const OrderCostSummary = ({
  itemCount,
  subtotal,
  shipping,
  tax = 0,
  taxLabel = 'Tax',
  total,
  currency = 'CAD',
  formatCurrency,
}) => {
  return (
    <div
      className="bg-gray-100 rounded-lg shadow-lg p-6 sticky top-4"
      data-testid="customer-cost-summary"
    >
      <div className="space-y-3 mb-4">
        <div className="flex justify-between text-gray-700">
          <span>Total Items</span>
          <span className="font-semibold">{itemCount}</span>
        </div>
        <div className="flex justify-between text-gray-700">
          <span>Total Cost</span>
          <span className="font-semibold">{formatCurrency(subtotal)}</span>
        </div>
        <div className="flex justify-between text-gray-700">
          <span>Shipping &amp; Handling*</span>
          <span className="font-semibold">{formatCurrency(shipping)}</span>
        </div>
        <div className="flex justify-between text-gray-700" data-testid="customer-tax-row">
          <span title={tax > 0 ? `Tax: ${taxLabel}` : undefined}>
            {tax > 0 ? `Tax (${taxLabel})` : 'Tax'}
          </span>
          <span
            className="font-semibold"
            data-testid="customer-tax-amount"
          >
            {tax > 0 ? formatCurrency(tax) : '--'}
          </span>
        </div>
        <div className="border-t-2 border-gray-300 pt-3">
          <div className="flex justify-between text-xl font-bold text-gray-900">
            <span>Total ({currency})</span>
            <span data-testid="customer-grand-total">{formatCurrency(total)}</span>
          </div>
        </div>
      </div>

      <Button
        type="submit"
        className="w-full bg-[#4A7C59] hover:bg-[#3d6849] text-white py-4 text-lg"
        data-testid="customer-continue-btn"
      >
        CONTINUE
      </Button>

      <p className="text-xs text-gray-500 mt-3 italic">
        * estimated shipping costs are subject to change upon order confirmation
      </p>
    </div>
  );
};
